import { Divider, message, Typography } from 'antd';
import { navigate } from 'gatsby';
import PropTypes from 'prop-types';
import React from 'react';
import { connect, useSelector } from 'react-redux';

import { useSubmitMassOffering } from '../../api/actions/useSubmitMassOffering';
import { QUERY_PARAM_KEYS, QUERY_PARAM_VALUES } from '../../constants/massOfferings/queryParamKeys';
import { massPropTypes } from '../../propTypes/mass';
import selectors from '../../store/selectors';

import styles from './AddMassOffering.module.scss';
import AddMassOfferingForm from './AddMassOfferingForm';
import { MassDateTime } from './components/MassDateTime/MassDateTime';
import { FORM_FIELDS, MASS_OFFERINGS_PAYMENT_TYPES } from './constants';

const enhance = connect(state => ({
  accountId: selectors.getAccountId(state),
}));

const navigateToFreeTransactionConfirmation = pathname => {
  const searchParams = new URLSearchParams({
    [QUERY_PARAM_KEYS.source]: QUERY_PARAM_VALUES.TRANSACTION,
    [QUERY_PARAM_KEYS.sourceType]: QUERY_PARAM_VALUES.SOURCE_INTERNAL,
    [QUERY_PARAM_KEYS.state]: QUERY_PARAM_VALUES.SUCCESS,
  });
  navigate(`${pathname}?${searchParams.toString()}`);
};

const navigateToPayLaterTransactionConfirmation = pathname => {
  const searchParams = new URLSearchParams({
    [QUERY_PARAM_KEYS.source]: QUERY_PARAM_VALUES.TRANSACTION,
    [QUERY_PARAM_KEYS.sourceType]: QUERY_PARAM_VALUES.SOURCE_INTERNAL,
    [QUERY_PARAM_KEYS.state]: QUERY_PARAM_VALUES.SUCCESS,
    [QUERY_PARAM_KEYS.paymentType]: QUERY_PARAM_VALUES.LATER,
  });
  navigate(`${pathname}?${searchParams.toString()}`);
};

const handleCreateMassOffering = (error, payload) => {
  if (error) {
    if (payload.code === 'conflict') {
      message.error('Wybrana Msza Święta nie ma już wolnych intencji. Wybierz inną Mszę Świętą', 8);
    } else {
      message.error('Wystąpił problem połączenia. Odśwież stronę i spróbuj ponownie', 8);
    }
  } else if (payload.tpayLink) {
    // eslint-disable-next-line no-restricted-globals
    location.href = payload.tpayLink;
  } else if (payload.paymentType === MASS_OFFERINGS_PAYMENT_TYPES.toBeChosen) {
    // eslint-disable-next-line no-restricted-globals
    navigateToPayLaterTransactionConfirmation(location.pathname);
  } else {
    // eslint-disable-next-line no-restricted-globals
    navigateToFreeTransactionConfirmation(location.pathname);
  }
};

const convertProviderParamsToUnderscore = providerParams => {
  if (!providerParams) {
    return null;
  }

  return {
    provider_id: providerParams.providerId,
    slots: providerParams.slots?.map(slot => ({
      primary_id: slot.primaryId,
      secondary_id: slot.secondaryId,
      book_id: slot.bookId,
    })),
  };
};

const AddMassOffering = ({ selectedMass, accountId }) => {
  const { loading, mutate } = useSubmitMassOffering();
  const isServiceFeeEnabled = useSelector(selectors.selectIsServiceFeeEnabled);

  const formatValues = values => {
    const paymentType = values[FORM_FIELDS.PAYMENT_TYPE];

    return {
      donator_email: values[FORM_FIELDS.DONATOR_EMAIL],
      donator_name: values[FORM_FIELDS.DONATOR_NAME],
      donator_phone: values[FORM_FIELDS.DONATOR_PHONE],
      mass_id: selectedMass.id,
      offering_content: values[FORM_FIELDS.OFFERING_CONTENT],
      payment_type: paymentType,
      provider_params: convertProviderParamsToUnderscore(selectedMass.providerParams),
      mass_date: selectedMass.massDate,
      offering_amount:
        paymentType === MASS_OFFERINGS_PAYMENT_TYPES.toBeChosen
          ? 0
          : values[FORM_FIELDS.TOTAL_AMOUNT_TO_PAY],
      ...(isServiceFeeEnabled && {
        service_fee_covered: values[FORM_FIELDS.USER_COVERS_SERVICE_FEE],
      }),
    };
  };

  const onSubmit = async values => {
    const { error, payload } = await mutate({
      accountId,
      formValues: formatValues(values),
    });

    handleCreateMassOffering(error, payload);
  };

  return (
    <div className={styles.self}>
      <Typography.Title level={2}>Intencje mszalne</Typography.Title>

      <MassDateTime mass={selectedMass} />

      <Divider />

      <Typography.Title className={styles.subtitle} level={4}>
        Treść intencji
      </Typography.Title>
      <Typography.Text>
        Możemy kontaktować się z Tobą, jeśli intencja modlitwy nie będzie dla nas jasna.
      </Typography.Text>

      <AddMassOfferingForm selectedMass={selectedMass} onSubmit={onSubmit} loading={loading} />
    </div>
  );
};

AddMassOffering.propTypes = {
  selectedMass: massPropTypes,
  accountId: PropTypes.string,
  massOfferingAmount: PropTypes.shape({
    predefinedValues: PropTypes.array,
    allowOwnAmount: PropTypes.bool,
    minimalValue: PropTypes.number,
  }),
};

export default enhance(AddMassOffering);
